import React, { useEffect, useState } from 'react';
import { TrackJS } from 'trackjs';
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import {
  MeetingRoomBookingViewPanel, selectAttendees,
  setAttendees,
  setDialogPanel
} from "../../models/meeting-room-booking-dialog.duck";
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import TextField from '@shared/ui/atoms/TextField';
import { findUserRequest } from '@features/settings/services/UserService';
import { BasicUserInfo } from '@shared/models/user.models';
import { naturalSort } from '@shared/utils/ArrayUtils';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { Checkbox } from '@shared/ui/atoms/Checkbox';

export function AddRemoveAttendeesPanel(props: Props) {
  const dispatch = useDispatch();
  const currentAttendees = useSelector(selectAttendees);
  const [searchQuery, setSearchField] = useState('');
  const [users, setUsers] = useState<BasicUserInfo[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<BasicUserInfo[]>([]);

  useEffect(() => {
    const id = setTimeout(() => {
      findUserRequest(searchQuery)
        .then((result: BasicUserInfo[]) => naturalSort(result, 'fullName'))
        .then((result: BasicUserInfo[]) => setUsers(result))
        .catch((e: any) => {
          // Search failure shows an empty result list — track it so it isn't invisible.
          TrackJS?.track(`[AddRemoveAttendeesPanel] user search failed: ${String(e?.message ?? e).slice(0, 200)}`);
        });
    }, 250);

    return () => {
      clearTimeout(id);
    }
  }, [searchQuery]);

  const onCancelClicked = () => {
    setSelectedUsers([]);
    setUsers([]);
    setSearchField('');
    dispatch(setDialogPanel(MeetingRoomBookingViewPanel.MeetingRoomBooking));
  }

  const onFinishedClicked = () => {
    dispatch(setAttendees([...currentAttendees, ...selectedUsers]));
    setSelectedUsers([]);
    setUsers([]);
    setSearchField('');
    dispatch(setDialogPanel(MeetingRoomBookingViewPanel.MeetingRoomBooking));
  }

  const onUserSelect = (user: BasicUserInfo) => {
    if (selectedUsers.find((u: BasicUserInfo) => u.userId === user.userId)) {
      setSelectedUsers(selectedUsers.filter((u: BasicUserInfo) => u.userId !== user.userId))
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  }

  return (
    <>
      <Content>
        <HeadlineMedium style={{marginBottom: 24}}>Add attendee</HeadlineMedium>
        <TextField onChange={setSearchField} fullWidth={true} value={searchQuery} label={'Search for an existing user'} />

        <SearchResults>
          {users.map((user: BasicUserInfo, key: number) => (
            <UserInfoRow key={key} className={'searchDialog__result'}>
              <UserAvatarAndName firstName={user.firstName} lastName={user.lastName} />
              <Checkbox value={!!selectedUsers.find(su => su.userId === user.userId)} onChange={() => onUserSelect(user)} />
            </UserInfoRow>
          ))}
        </SearchResults>
        <Controls>
          <OutlineButton text={'button.cancel'} click={onCancelClicked} />
          <PrimaryButton text={'button.done'} click={onFinishedClicked} />
        </Controls>
      </Content>
    </>
  )
}

interface Props {
}


const Content = styled.div`
  padding: 24px 56px 0 56px;
  flex: 1;
  display: flex;
  flex-direction: column;
`

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  padding-bottom: 24px;
`

const SearchResults = styled.div`
  height: 350px;
  overflow-y: auto;
`

const UserInfoRow = styled.div<any>`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  .userAvatarAndName {
    cursor: pointer;
  }
`
